import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { LocaleLink as Link } from "../../i18n/LocaleLink";
import { Headphones, Loader2, Pause, Play, Radio, X } from "lucide-react";

import { useAudioPreviewGate } from "../useAudioPreviewGate";
import { ServerAudioPlayer, type StartParams } from "./server-player";
import { LocalRagaPlayer } from "./local-raga-player";

export type PersistentAudioStatus = "idle" | "loading" | "playing" | "paused" | "error";

export interface PersistentAudioMeta {
  ownerKey: string;
  kind: "radio" | "life-music";
  title: string;
  subtitle: string;
  href: string;
  chartId?: string | null;
}

interface PersistentAudioValue {
  meta: PersistentAudioMeta | null;
  status: PersistentAudioStatus;
  error: string | null;
  chunkLabel: string | null;
  preview: ReturnType<typeof useAudioPreviewGate>;
  isOwner: (ownerKey: string) => boolean;
  start: (meta: PersistentAudioMeta, params: StartParams) => Promise<void>;
  pause: () => void;
  resume: () => Promise<void>;
  stop: () => void;
}

const PersistentAudioContext = createContext<PersistentAudioValue | null>(null);

export function PersistentAudioProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const playerRef = useRef<ServerAudioPlayer | null>(null);
  const localPlayerRef = useRef<LocalRagaPlayer | null>(null);
  const generationRef = useRef(0);
  const ownerRef = useRef<string | null>(null);
  const [meta, setMeta] = useState<PersistentAudioMeta | null>(null);
  const [status, setStatus] = useState<PersistentAudioStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [chunkLabel, setChunkLabel] = useState<string | null>(null);

  const pause = useCallback(() => {
    playerRef.current?.pause();
    localPlayerRef.current?.pause();
  }, []);

  const preview = useAudioPreviewGate({
    chartId: meta?.chartId,
    playing: status === "playing",
    onLimitReached: pause,
  });

  const stop = useCallback(() => {
    generationRef.current += 1;
    const player = playerRef.current;
    const localPlayer = localPlayerRef.current;
    playerRef.current = null;
    localPlayerRef.current = null;
    player?.dispose();
    localPlayer?.stop();
    ownerRef.current = null;
    setMeta(null);
    setStatus("idle");
    setError(null);
    setChunkLabel(null);
    preview.reset();
  }, [preview.reset]);

  const resume = useCallback(async () => {
    await Promise.all([
      playerRef.current?.resume(),
      localPlayerRef.current?.resume(),
    ]);
    setStatus("playing");
  }, []);

  const start = useCallback(async (nextMeta: PersistentAudioMeta, params: StartParams) => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    const previousOwner = ownerRef.current;
    const previous = playerRef.current;
    const previousLocal = localPlayerRef.current;
    playerRef.current = null;
    localPlayerRef.current = null;
    previous?.dispose();
    previousLocal?.stop();

    if (previousOwner !== nextMeta.ownerKey) preview.reset();
    ownerRef.current = nextMeta.ownerKey;
    setMeta(nextMeta);
    setStatus("playing");
    setError(null);
    setChunkLabel(null);

    const localPlayer = new LocalRagaPlayer();
    try {
      await localPlayer.start(params);
      localPlayerRef.current = localPlayer;
      setChunkLabel("on-device ensemble");
    } catch {
      // Older embedded browsers may lack Web Audio. The authored HTML audio
      // path below remains available and preserves the previous behaviour.
      localPlayer.stop();
      setStatus("loading");
    }

    const player = new ServerAudioPlayer({
      onPlayingChange: (playing) => {
        if (generationRef.current !== generation) return;
        if (playing) {
          // Keep the synchronized local ensemble quietly underneath the
          // authored stream. If a slow next chunk leaves a gap, the raga and
          // tala continue instead of dropping to silence.
          localPlayer.setVolume(0.12);
        } else {
          localPlayer.setVolume(0.7);
        }
        setStatus(playing ? "playing" : "paused");
      },
      onChunkChange: (chunk) => {
        if (generationRef.current !== generation) return;
        setChunkLabel(chunk.sectionId || null);
      },
      onError: (reason) => {
        if (generationRef.current !== generation) return;
        localPlayer.setVolume(0.7);
        setError(t("ui.web.long_tail.audio.authored_unavailable", { raga: params.ragaName || t("ui.web.long_tail.audio.raga") }));
      },
    });
    playerRef.current = player;

    void player.start(params).catch((reason) => {
      if (generationRef.current === generation) {
        localPlayer.setVolume(0.7);
        setError(t("ui.web.long_tail.audio.authored_unavailable", { raga: params.ragaName || t("ui.web.long_tail.audio.raga") }));
        playerRef.current = null;
      }
      player.dispose();
    });
  }, [preview.reset, t]);

  useEffect(() => () => {
    generationRef.current += 1;
    playerRef.current?.dispose();
    playerRef.current = null;
    localPlayerRef.current?.stop();
    localPlayerRef.current = null;
  }, []);

  const value = useMemo<PersistentAudioValue>(() => ({
    meta,
    status,
    error,
    chunkLabel,
    preview,
    isOwner: (ownerKey) => meta?.ownerKey === ownerKey,
    start,
    pause,
    resume,
    stop,
  }), [meta, status, error, chunkLabel, preview, start, pause, resume, stop]);

  return (
    <PersistentAudioContext.Provider value={value}>
      {children}
      <PersistentAudioBar />
    </PersistentAudioContext.Provider>
  );
}

function PersistentAudioBar() {
  const { t } = useTranslation();
  const audio = usePersistentAudio();
  if (!audio.meta) return null;

  const playing = audio.status === "playing";
  const loading = audio.status === "loading";
  const Icon = audio.meta.kind === "radio" ? Radio : Headphones;

  return (
    <aside
      role="region"
      aria-label={t("ui.web.long_tail.audio.now_playing")}
      data-testid="persistent-audio-bar"
      className="fixed z-[70] start-3 end-3 bottom-[calc(4.75rem+env(safe-area-inset-bottom))] md:start-auto md:end-5 md:bottom-5 md:w-[420px] border border-brass-400/45 bg-walnut-900/95 shadow-2xl backdrop-blur-md"
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
        <Icon className="w-4 h-4 text-brass-300 shrink-0" aria-hidden="true" />
        <Link to={audio.meta.href} className="min-w-0 flex-1 group">
          <p className="font-caps text-[9px] tracking-[0.18em] uppercase text-brass-400">
            {t("ui.web.long_tail.audio.now_playing_kind", { kind: audio.meta.kind === "radio" ? t("ui.web.long_tail.audio.transit_radio") : t("ui.web.long_tail.audio.life_music") })}
          </p>
          <p className="font-display italic text-sm text-parchment-50 truncate group-hover:text-brass-100">
            {audio.meta.title}
          </p>
          <p className="font-mono text-[9px] text-parchment-500 truncate">
            {audio.chunkLabel ? `${audio.meta.subtitle} · ${audio.chunkLabel}` : audio.meta.subtitle}
          </p>
        </Link>
        <button
          type="button"
          onClick={() => playing ? audio.pause() : void audio.resume()}
          disabled={loading || audio.status === "error"}
          aria-label={playing ? t("ui.web.long_tail.audio.pause") : t("ui.web.long_tail.audio.resume")}
          className="w-9 h-9 inline-flex items-center justify-center border border-brass-400/35 text-brass-200 hover:bg-brass-500/10 disabled:opacity-40"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button
          type="button"
          onClick={audio.stop}
          aria-label={t("ui.web.long_tail.audio.stop_close")}
          className="p-2 text-parchment-500 hover:text-parchment-100"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {audio.error && (
        <p className="px-3 pb-2 font-body text-[11px] text-vermillion-300" role="status">
          {audio.error}
        </p>
      )}
    </aside>
  );
}

export function usePersistentAudio(): PersistentAudioValue {
  const context = useContext(PersistentAudioContext);
  if (!context) {
    throw new Error("usePersistentAudio must be used within PersistentAudioProvider");
  }
  return context;
}
